
import React from 'react';
import { Icon } from '@iconify/react';
import type { MappedLeadData, Interaction } from '../../types';
import { SocialIcon } from '../../utils/icons';
import { ActivityTimeline } from '../atoms/ActivityTimeline';

interface InteractionsTabProps {
  data: MappedLeadData;
  density?: 'standard' | 'compact';
}

const formatInteractionLabel = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const getCompactRoleTone = (role: Interaction['participantRole']) => {
  if (role === 'agent') return 'border-[#5260FF]/25 bg-[#5260FF]/10 text-[#AEB5FF]';
  if (role === 'lead') return 'border-[#15FBFF]/25 bg-[#15FBFF]/10 text-[#A9FCFF]';
  return 'border-white/10 bg-white/[0.045] text-white/55';
};

const getCompactStatusTone = (status?: NonNullable<Interaction['meta']>['status']) => {
  if (status === 'read' || status === 'completed') return 'text-emerald-300';
  if (status === 'scheduled') return 'text-cyan-300';
  if (status === 'missed') return 'text-rose-300';
  return 'text-white/45';
};

export const InteractionsTab: React.FC<InteractionsTabProps> = ({ data, density = 'standard' }) => {
  const interactions = data.interactions || [];

  const getInteractionIcon = (type: string, className: string) => (
    <SocialIcon platform={type} className={className} />
  );

  const formatTime = (isoString: string) => {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString: string) => {
      const date = new Date(isoString);
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // The compact density is used by the ISR workspace — full interactions only.
  // The standard density (the modal's Interactions tab) appends the notes
  // timeline below the interactions timeline, so all activity is in one view.
  if (density === 'compact') {
    return (
      <div className="flex h-full min-h-0 flex-col animate-fadeIn">
        <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-[#080A0F]/95 px-3 py-2.5 backdrop-blur">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/32">Interaction History</p>
            <p className="mt-0.5 truncate text-xs font-semibold text-white/60">Conversation timeline</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.045] px-2 py-1 text-[10px] font-black text-white/55">
            <Icon icon="bx:conversation" className="h-3.5 w-3.5 text-cyan-200/70" />
            {interactions.length}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar px-2 py-2">
          <div className="space-y-1.5">
            {interactions.map((item) => {
              const isAgent = item.participantRole === 'agent';
              const roleTone = getCompactRoleTone(item.participantRole);
              const statusTone = getCompactStatusTone(item.meta?.status);
              const subject = item.meta?.subject;

              return (
                <article
                  key={item.id}
                  className={`grid grid-cols-[30px_minmax(0,1fr)] gap-2 rounded-xl border px-2.5 py-2 transition-colors ${
                    isAgent
                      ? 'border-[#5260FF]/15 bg-[#5260FF]/[0.035] hover:border-[#5260FF]/25'
                      : 'border-white/8 bg-white/[0.025] hover:border-white/12 hover:bg-white/[0.04]'
                  }`}
                >
                  <div className={`mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg border ${isAgent ? 'border-[#5260FF]/25 bg-[#5260FF]/10 text-[#AEB5FF]' : 'border-white/10 bg-black/25 text-white/55'}`}>
                    {getInteractionIcon(item.type, 'h-3.5 w-3.5')}
                  </div>

                  <div className="min-w-0">
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                          <span className={`rounded-md border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.08em] ${roleTone}`}>
                            {formatInteractionLabel(item.participantRole)}
                          </span>
                          <span className="truncate text-[12px] font-bold text-white">{item.participantName}</span>
                        </div>
                        {subject && (
                          <p className="mt-0.5 truncate text-[11px] font-semibold text-white/55">Subject: {subject}</p>
                        )}
                      </div>

                      <time className="shrink-0 text-right text-[10px] font-semibold leading-4 text-white/35" dateTime={item.timestamp}>
                        <span className="block">{formatDate(item.timestamp)}</span>
                        <span className="block">{formatTime(item.timestamp)}</span>
                      </time>
                    </div>

                    <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-[12px] leading-5 text-[#C9D1E2]">
                      {item.content}
                    </p>

                    <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-white/[0.055] pt-1.5 text-[10px] font-bold">
                      <span className="inline-flex items-center gap-1 text-white/40">
                        <Icon icon="bx:send" className="h-3 w-3" />
                        {formatInteractionLabel(item.type)}
                      </span>
                      <span className={`inline-flex items-center gap-1 uppercase tracking-[0.08em] ${statusTone}`}>
                        <Icon icon="bx:check-circle" className="h-3 w-3" />
                        {item.meta?.status || 'sent'}
                      </span>
                      {item.meta?.duration && (
                        <span className="inline-flex items-center gap-1 text-white/40">
                          <Icon icon="bx:time-five" className="h-3 w-3" />
                          {item.meta.duration}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}

            </div>

          {/* Notes & Activity (read-only) — appended below the interactions list
              in the compact variant used by the lead details modal. */}
          <div className="mt-6 border-t border-white/8 pt-4">
            <p className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-white/32">Notes &amp; Activity</p>
            <ActivityTimeline lead={data} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="flex items-center justify-between mb-6 px-2">
        <h2 className="text-xl font-bold text-white flex items-center gap-3">
          <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
          </div>
          Notes &amp; Activity
        </h2>
        <span className="text-[10px] font-semibold tracking-[0.12em] text-white/35">
          {[
            ...(data.isrNotes?.notes || []),
            ...(data.interactions || []),
          ].length} events
        </span>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-2">
        <ActivityTimeline lead={data} />
      </div>
    </div>
  );
};
